import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import PublicLayout from '@/components/hydlib/PublicLayout';

const mono = "'Space Mono', monospace";
const serifDisplay = "'Libre Caslon Display', serif";

const policies = [
  {
    id: 'terms',
    title: 'Terms and Conditions',
    tag: 'the rules',
    accent: '--a1',
    content: `
## Terms and Conditions

**Last Updated: January 2026**

Welcome to The Hyderabad Library. By accessing or using our website (thehydlibrary.in) and services, you agree to be bound by these Terms and Conditions.

### 1. Definitions

- **"Library"** refers to The Hyderabad Library, located at 2nd Floor, BBR Forum, Road No. 2 Banjara Hills, Hyderabad (same building as KSR Eyecare).
- **"Member"** refers to any individual who has registered and subscribed to our services.
- **"Services"** refers to book lending, catalog browsing, and related library services offered through our platform.

### 2. Membership

- Membership is available to individuals aged 18 years or older.
- Members must provide accurate and complete registration information.
- Each membership is personal and non-transferable.
- Membership fee is ₹500 per month, billed automatically.

### 3. Borrowing Rules

- Members may borrow up to 5 books at a time.
- Standard borrowing period is 21 days from the date of pickup.
- Extensions may be requested through the website, subject to availability.
- Late returns incur a fee of ₹10 per day per book.

### 4. Member Responsibilities

- Handle borrowed books with care and return them in the same condition.
- Report any damage or loss immediately.
- Pay all outstanding fees before borrowing additional books.
- Keep login credentials secure and confidential.

### 5. Book Condition and Liability

- Members are responsible for any damage, loss, or theft of borrowed materials.
- Replacement cost plus a processing fee will be charged for lost or damaged books.
- The Library reserves the right to assess the condition of returned materials.

### 6. Account Termination

- The Library reserves the right to suspend or terminate memberships for:
  - Violation of these terms
  - Non-payment of fees
  - Misuse of library materials
  - Fraudulent activity

### 7. Intellectual Property

- All content on this website, including text, graphics, logos, and software, is the property of The Hyderabad Library.
- Members may not reproduce, distribute, or create derivative works without permission.

### 8. Limitation of Liability

- The Library is not liable for any indirect, incidental, or consequential damages.
- Our total liability shall not exceed the membership fees paid in the preceding 12 months.

### 9. Modifications

- We reserve the right to modify these terms at any time.
- Continued use of services after changes constitutes acceptance of new terms.
- Members will be notified of significant changes via email.

### 10. Governing Law

- These terms are governed by the laws of India.
- Any disputes shall be subject to the exclusive jurisdiction of courts in Hyderabad, Telangana.

### 11. Contact Information

For questions about these Terms and Conditions:
- **Email:** thehyderabadlibrary@gmail.com
- **Phone:** 7901662333
- **Address:** The Hyderabad Library, 2nd Floor, BBR Forum, Road No. 2 Banjara Hills, Hyderabad (same building as KSR Eyecare)
    `
  },
  {
    id: 'privacy',
    title: 'Privacy Policy',
    tag: 'your data',
    accent: '--a3',
    content: `
## Privacy Policy

**Last Updated: January 2026**

The Hyderabad Library ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information.

### 1. Information We Collect

**Personal Information:**
- Name and contact details (email, phone number)
- Address (for delivery services)
- Payment information (processed securely through Razorpay)
- Borrowing history and preferences

**Automatically Collected Information:**
- Browser type and device information
- IP address and location data
- Website usage patterns and analytics

### 2. How We Use Your Information

We use your information to:
- Process membership registrations and payments
- Manage book borrowing and returns
- Send notifications about due dates, holds, and reservations
- Communicate important updates and announcements
- Improve our services and user experience
- Comply with legal obligations

### 3. Information Sharing

We do not sell or rent your personal information. We may share information with:
- **Payment Processors:** Razorpay for secure payment processing
- **Service Providers:** Third parties who assist in our operations
- **Legal Requirements:** When required by law or to protect our rights

### 4. Data Security

We implement appropriate security measures including:
- Encrypted data transmission (SSL/TLS)
- Secure password storage
- Regular security assessments
- Access controls and authentication

### 5. Your Rights

You have the right to:
- Access your personal information
- Correct inaccurate data
- Request deletion of your data (subject to legal requirements)
- Opt-out of marketing communications
- Export your borrowing history

### 6. Cookies

We use cookies to:
- Maintain your session and login status
- Remember your preferences
- Analyze website traffic and usage

You can control cookies through your browser settings.

### 7. Children's Privacy

Our services are not intended for individuals under 18 years of age. We do not knowingly collect information from children.

### 8. Data Retention

We retain your information for:
- Active accounts: Duration of membership plus 2 years
- Borrowing records: 5 years for internal records
- Payment records: As required by tax and financial regulations

### 9. Third-Party Links

Our website may contain links to third-party sites. We are not responsible for their privacy practices.

### 10. Changes to This Policy

We may update this Privacy Policy periodically. We will notify you of significant changes via email or website notification.

### 11. Contact Us

For privacy-related inquiries:
- **Email:** thehyderabadlibrary@gmail.com
- **Phone:** 7901662333
- **Address:** The Hyderabad Library, 2nd Floor, BBR Forum, Road No. 2 Banjara Hills, Hyderabad (same building as KSR Eyecare)
    `
  },
  {
    id: 'refund',
    title: 'Refund Policy',
    tag: 'money back',
    accent: '--a5',
    content: `
## Refund Policy

**Last Updated: January 2026**

This Refund Policy outlines the terms and conditions for refunds at The Hyderabad Library.

### 1. Subscription Refunds

**Within 3 Days of Initial Subscription:**
- Full refund available for first-time subscribers
- Request must be made within 3 calendar days of payment
- Refund will be processed to the original payment method
- Processing time: 5-7 business days

**After 3 Days:**
- No refund will be issued
- Membership remains active until the end of the current billing period
- You may continue to use all library services until expiration

**Renewal Payments:**
- No refunds for automatic renewal payments
- Cancel before renewal date to avoid charges

### 2. How to Request a Refund

To request a refund within the eligible period:

1. **Email us at:** thehyderabadlibrary@gmail.com
2. **Include:**
   - Your registered email address
   - Membership number
   - Reason for refund request
   - Date of payment

3. **Or call us at:** 7901662333 (Mon-Sat, 11 AM - 5 PM)

### 3. Refund Processing

- Approved refunds are processed within 5-7 business days
- Refunds are credited to the original payment method
- Bank processing may take additional 3-5 days
- You will receive email confirmation once processed

### 4. Non-Refundable Items

The following are not eligible for refunds:
- Late fees and penalties
- Lost or damaged book charges
- Processing fees
- Payments made more than 3 days ago (for new subscriptions)
- Renewal payments after auto-debit

### 5. Partial Refunds

Partial refunds may be issued in special circumstances:
- Service unavailability exceeding 7 consecutive days
- Technical issues preventing access to services
- At the sole discretion of library management

### 6. Dispute Resolution

If you believe a charge is incorrect:
1. Contact us within 30 days of the charge
2. Provide transaction details and reason for dispute
3. We will investigate and respond within 7 business days

### 7. Contact Information

For refund requests or queries:
- **Email:** thehyderabadlibrary@gmail.com
- **Phone:** 7901662333
- **Hours:** Monday to Saturday, 11 AM - 5 PM
- **Address:** The Hyderabad Library, 2nd Floor, BBR Forum, Road No. 2 Banjara Hills, Hyderabad (same building as KSR Eyecare)
    `
  },
  {
    id: 'cancellation',
    title: 'Cancellation Policy',
    tag: 'parting ways',
    accent: '--a4',
    content: `
## Cancellation Policy

**Last Updated: January 2026**

This policy explains how to cancel your membership and what happens when you do.

### 1. How to Cancel Your Subscription

**Online Cancellation:**
1. Log in to your account at thehydlibrary.in
2. Go to My Library → Payments
3. Navigate to the Membership section
4. Click "Cancel Subscription"
5. Confirm your cancellation

**Contact Cancellation:**
- Email: thehyderabadlibrary@gmail.com
- Phone: 7901662333 (Mon-Sat, 11 AM - 5 PM)
- Include your registered email and membership number

### 2. What Happens When You Cancel

**Immediate Effects:**
- No future automatic payments will be charged
- Your account is marked for cancellation

**Continued Access:**
- You retain full access until the end of your current billing period
- All borrowed books must be returned by the membership end date
- Outstanding fees must be cleared

**After Membership Ends:**
- You can no longer borrow books
- You can no longer place holds
- Your account becomes inactive
- Your borrowing history is retained for your records

### 3. Refund Eligibility on Cancellation

**Within 3 Days of First Subscription:**
- Cancel and receive a full refund
- Contact us immediately to process

**After 3 Days:**
- No refund issued
- Access continues until billing period ends

**After Next Billing Cycle Starts:**
- No refund for the new payment
- Cancellation prevents future charges only

### 4. Return of Materials

Before your membership ends:
- Return all borrowed books
- Clear any outstanding late fees
- Failure to return books will result in replacement charges

### 5. Reactivating Your Membership

If you wish to rejoin after cancellation:
- Simply subscribe again through My Library → Payments
- Your previous account and history will be restored
- New billing cycle starts from reactivation date

### 6. Cancellation by Library

We reserve the right to cancel memberships for:
- Violation of Terms and Conditions
- Non-return of borrowed materials
- Fraudulent activity
- Non-payment of outstanding fees

In such cases:
- You will be notified via email
- Outstanding fees remain payable
- All borrowed materials must be returned immediately

### 7. Questions About Cancellation

For any questions regarding cancellation:
- **Email:** thehyderabadlibrary@gmail.com
- **Phone:** 7901662333
- **Hours:** Monday to Saturday, 11 AM - 5 PM
- **Address:** The Hyderabad Library, 2nd Floor, BBR Forum, Road No. 2 Banjara Hills, Hyderabad (same building as KSR Eyecare)
    `
  }
];


function renderLine(line, index) {
  if (line.startsWith('## ')) {
    return <h2 key={index} style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 32, margin: '0 0 6px' }}>{line.replace('## ', '')}</h2>;
  }
  if (line.startsWith('### ')) {
    return <h3 key={index} style={{ fontFamily: mono, fontWeight: 700, fontSize: 13, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--a1)', margin: '28px 0 10px' }}>{line.replace('### ', '')}</h3>;
  }
  if (line.startsWith('**') && line.endsWith('**')) {
    return <p key={index} style={{ fontWeight: 700, fontSize: 15, margin: '14px 0 4px' }}>{line.replace(/\*\*/g, '')}</p>;
  }
  if (line.startsWith('- ') || /^\s+- /.test(line)) {
    const nested = /^\s+- /.test(line);
    return (
      <div key={index} style={{ display: 'flex', gap: 10, fontSize: 15, lineHeight: 1.65, marginLeft: nested ? 22 : 0, marginBottom: 3 }}>
        <span style={{ color: 'var(--a3)', flexShrink: 0 }}>—</span>
        <span>{line.trim().replace(/^- /, '').replace(/\*\*(.+?)\*\*/g, '$1')}</span>
      </div>
    );
  }
  if (/^\d+\.\s/.test(line.trim())) {
    return (
      <div key={index} style={{ display: 'flex', gap: 10, fontSize: 15, lineHeight: 1.65, marginBottom: 3 }}>
        <span style={{ fontFamily: mono, fontWeight: 700, fontSize: 13, color: 'var(--a1)', flexShrink: 0 }}>{line.trim().match(/^\d+/)[0]}.</span>
        <span>{line.trim().replace(/^\d+\.\s/, '').replace(/\*\*(.+?)\*\*/g, '$1')}</span>
      </div>
    );
  }
  if (line.trim()) {
    return <p key={index} style={{ fontSize: 15, lineHeight: 1.7, margin: '8px 0' }}>{line.replace(/\*\*(.+?)\*\*/g, '$1')}</p>;
  }
  return null;
}

export default function Policies() {
  const { policyType } = useParams();
  const [activePolicy, setActivePolicy] = useState(policyType || 'terms');

  useEffect(() => {
    if (policyType && policies.find(p => p.id === policyType)) {
      setActivePolicy(policyType);
      window.scrollTo(0, 0);
    }
  }, [policyType]);

  const currentPolicy = policies.find(p => p.id === activePolicy) || policies[0];

  return (
    <PublicLayout theme="archive" grid="--a1" gridStrength={22}>
      <div className="hydlib-scallop" style={{ backgroundImage: 'radial-gradient(circle at 50% 0, var(--a2) 0 5px, transparent 6px)' }} />
      <div className="hydlib-pad" style={{ position: 'relative', maxWidth: 900, margin: '0 auto', padding: '64px 48px 80px', boxSizing: 'border-box' }}>
        <h1 style={{ fontFamily: serifDisplay, fontWeight: 400, fontSize: 48, margin: '0 0 8px' }} data-testid="policies-heading">
          <span style={{ background: 'var(--bg)', boxShadow: '8px 0 0 var(--bg), -8px 0 0 var(--bg)' }}>The fine print</span>
        </h1>
        <p style={{ fontFamily: mono, fontSize: 13, letterSpacing: 2, textTransform: 'uppercase', color: 'var(--a1)', margin: '0 0 8px' }}>
          <span style={{ background: 'var(--bg)', boxShadow: '6px 0 0 var(--bg), -6px 0 0 var(--bg)' }}>Legal &amp; policies</span>
        </p>
        <p style={{ fontSize: 15.5, opacity: 0.85, margin: '0 0 36px', maxWidth: 560, position: 'relative' }}>
          <span style={{ background: 'var(--bg)', boxShadow: '4px 0 0 var(--bg), -4px 0 0 var(--bg)' }}>Please review our policies below. By using our services, you agree to these terms.</span>
        </p>

        {/* Policy tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 32 }}>
          {policies.map((policy) => {
            const active = activePolicy === policy.id;
            return (
              <Link
                key={policy.id}
                to={`/policies/${policy.id}`}
                data-testid={`policy-tab-${policy.id}`}
                style={{
                  fontFamily: mono,
                  fontWeight: 700,
                  fontSize: 12,
                  letterSpacing: 1.5,
                  textTransform: 'uppercase',
                  padding: '9px 18px',
                  border: '1px solid var(--line)',
                  background: active ? `var(${policy.accent})` : 'var(--paper)',
                  color: active ? `var(--o${policy.accent.slice(-1)})` : 'var(--ink)',
                  boxShadow: active ? '3px 4px 0 color-mix(in oklch, var(--line) 35%, transparent)' : 'none',
                }}
              >
                {policy.title}
              </Link>
            );
          })}
        </div>

        {/* Policy content */}
        <div style={{ position: 'relative', border: '1px solid var(--line)', borderTop: `6px solid var(${currentPolicy.accent})`, background: 'var(--paper)', padding: '36px 38px' }} data-testid="policy-content">
          <span style={{ position: 'absolute', top: -16, right: 22, fontFamily: "'Caveat', cursive", fontSize: 22, fontWeight: 600, color: `var(${currentPolicy.accent})`, transform: 'rotate(-2deg)', background: 'var(--bg)', padding: '0 8px' }}>{currentPolicy.tag}</span>
          {currentPolicy.content.split('\n').map(renderLine)}
        </div>

        {/* Contact strip */}
        <div style={{ marginTop: 36, border: '1px solid var(--line)', borderLeft: '6px solid var(--a2)', background: 'var(--paper)', padding: '20px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontFamily: serifDisplay, fontSize: 20, marginBottom: 4 }}>Questions about our policies?</div>
            <div style={{ fontSize: 14, opacity: 0.85 }}>
              Write to <a href="mailto:thehyderabadlibrary@gmail.com" style={{ color: 'var(--a3)' }}>thehyderabadlibrary@gmail.com</a> or call <a href="tel:+917901662333" style={{ color: 'var(--a3)' }}>7901662333</a>
            </div>
          </div>
          <Link to="/contact" className="hydlib-highlight" data-testid="policies-contact-link" style={{ fontFamily: mono, fontWeight: 700, fontSize: 12, letterSpacing: 1.5, textTransform: 'uppercase', color: 'var(--ink)', padding: '9px 18px', background: `linear-gradient(104deg, transparent 1.5%, color-mix(in oklch, var(--a2) 62%, var(--paper)) 5%, color-mix(in oklch, var(--a2) 46%, var(--paper)) 52%, color-mix(in oklch, var(--a2) 68%, var(--paper)) 94%, transparent 98.5%)` }}>Contact us →</Link>
        </div>
      </div>
    </PublicLayout>
  );
}
